import {Context} from 'chartjs-plugin-datalabels';

import {barChartOptions} from '../../layout/chart/bar-chart/bar-chart-helper';
import {BarDataType, ChartJsBarOptionsType} from '../../layout/chart/bar-chart/bar-chart-type';
import {gridColor, labelColor} from '../../layout/chart/chart-const';
import {customReactChartTooltip} from '../../layout/chart/utils/chart-helper';
import {WeekDaysEnum} from '../../provider/locale/locale-context-const';
import {LangKeyType} from '../../provider/locale/translation/type';
import {ChartTooltip} from '../chart-tooltip/chart-tooltip';
import {ChartTooltipRow} from '../chart-tooltip/chart-tooltip-row';
import {ChartTooltipTitle} from '../chart-tooltip/chart-tooltip-title';
import {DefaultTooltipStaticPropsType} from '../chart-tooltip/default-chart-tooltip';

import {WorkloadByHoursAndWeekDaysType} from './workload-type';
import * as styles from './workload.scss';

export const WEEK_DAY_TO_LOCALIZED_STRING: Record<WeekDaysEnum, LangKeyType> = {
    [WeekDaysEnum.monday]: 'WEEK_DAY__SHORT__MONDAY',
    [WeekDaysEnum.tuesday]: 'WEEK_DAY__SHORT__TUESDAY',
    [WeekDaysEnum.wednesday]: 'WEEK_DAY__SHORT__WEDNESDAY',
    [WeekDaysEnum.thursday]: 'WEEK_DAY__SHORT__THURSDAY',
    [WeekDaysEnum.friday]: 'WEEK_DAY__SHORT__FRIDAY',
    [WeekDaysEnum.saturday]: 'WEEK_DAY__SHORT__SATURDAY',
    [WeekDaysEnum.sunday]: 'WEEK_DAY__SHORT__SUNDAY',
} as const;

function formatHours(hour: string) {
    return `${hour.padStart(2, '0')}:00`;
}

function getBarColorByValue(value: number, maxValue: number): string {
    const delta = (value * 100) / maxValue;

    if (delta < 25) {
        return styles.bar_color_1;
    }

    if (delta < 50) {
        return styles.bar_color_2;
    }

    if (delta < 75) {
        return styles.bar_color_3;
    }

    return styles.bar_color_4;
}

export function deserializeDataForChartByWeekDay(
    rawChartData: WorkloadByHoursAndWeekDaysType,
    weekDay: WeekDaysEnum
): BarDataType {
    const workloadByWeekDay = rawChartData[weekDay];

    if (!workloadByWeekDay) {
        return {
            labels: [],
            datasets: [],
        };
    }

    const labels: Array<string> = Object.keys(workloadByWeekDay).map(formatHours);

    const maxValue = Math.max(...Object.values(workloadByWeekDay));
    const datasets = [
        {
            label: '',
            values: Object.values(workloadByWeekDay),
            color: Object.values(workloadByWeekDay).map((value) => getBarColorByValue(value, maxValue)),
            borderRadius: 4,
        },
    ];

    return {
        labels,
        datasets,
    };
}

export function getWorkloadChartOptions(
    tooltipOptions: DefaultTooltipStaticPropsType & {tooltipTitle: string},
    yMax: number
): ChartJsBarOptionsType {
    return {
        ...barChartOptions,
        scales: {
            x: {
                type: 'category',
                stacked: true,
                grid: {
                    color: gridColor,
                    display: false,
                    drawTicks: false,
                },
                ticks: {
                    color: labelColor,
                    padding: 10,
                    maxRotation: 0,
                    maxTicksLimit: 8,
                },
            },
            y: {
                stacked: true,
                border: {
                    display: false,
                },
                grid: {
                    color: gridColor,
                    drawTicks: false,
                },
                ticks: {
                    color: labelColor,
                    padding: 10,
                    maxTicksLimit: 10,
                    callback(value) {
                        return Math.floor(Number(value));
                    },
                },
                suggestedMax: yMax,
                suggestedMin: 0,
            },
        },
        plugins: {
            ...barChartOptions.plugins,
            legend: {
                display: false,
            },
            tooltip: {
                enabled: false,
                yAlign: 'top',
                xAlign: 'center',
                position: 'nearest',
                external: (context: Context) => {
                    customReactChartTooltip({
                        context,
                        componentRenderer: (index) => {
                            return (
                                <ChartTooltip>
                                    <ChartTooltipTitle title={tooltipOptions.tooltipTitle} />
                                    <ChartTooltipRow
                                        label={tooltipOptions.data.labels?.[index] || ''}
                                        value={tooltipOptions.data.datasets?.[0]?.values[index] || ''}
                                    />
                                </ChartTooltip>
                            );
                        },
                    });
                },
            },
        },
    };
}
